import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Button, Dropdown, ButtonGroup} from 'react-bootstrap';
import ReactTooltip from 'react-tooltip';
import {isMac} from '../isMac';
import {BRUSH} from 'rc-canvas/lib/DrawingModes';
import Mousetrap from 'mousetrap';
import {Defaults} from '../Constants';
import {maybe} from '../utils';
import classNames from 'classnames';

export default class EditHistory extends Component {
    static propTypes = {
        history: PropTypes.array.isRequired,
        onToggleVisible: PropTypes.func.isRequired,
        onDelete: PropTypes.func.isRequired,
        onReset: PropTypes.func.isRequired,
        onUndoReset: PropTypes.func.isRequired,
        onUndoDelete: PropTypes.func.isRequired,
        onHighlightHistoryItem: PropTypes.func,
        hasUndoReset: PropTypes.bool,
        hasUndoDelete: PropTypes.bool,
        rightAlignMenu: PropTypes.bool,
        height: PropTypes.number,
    };

    componentDidMount() {
        Mousetrap.bind('mod+z', (e) => {
            e.preventDefault();
            this.handleUndo();
        });

        Mousetrap.bind('mod+y', (e) => {
            e.preventDefault();
            this.handleRedo();
        });
    }

    componentWillUnmount() {
        // noinspection JSUnresolvedFunction
        Mousetrap.unbind(['mod+z', 'mod+y']);
    }

    getFirstVisibleUuid = () => {
        const {history} = this.props;
        for (let i = history.length - 1; i >= 0; --i) {
            const historyItem = history[i];
            const {uuid, deleted, hidden} = historyItem;
            if (deleted) {
                continue;
            }
            if (!hidden) {
                return uuid;
            }
        }
        return null;
    };

    getFirstHiddenUuid = () => {
        const {history} = this.props;
        for (let i = history.length - 1; i >= 0; --i) {
            const historyItem = history[i];
            const {uuid, deleted, hidden} = historyItem;
            if (deleted) {
                continue;
            }
            if (hidden) {
                return uuid;
            }
        }
        return null;
    };

    handleUndo = () => {
        const firstVisibleUuid = this.getFirstVisibleUuid();
        if (firstVisibleUuid) {
            this.props.onToggleVisible(firstVisibleUuid);
        }
    };

    handleRedo = () => {
        const firstHiddenUuid = this.getFirstHiddenUuid();
        if (firstHiddenUuid) {
            this.props.onToggleVisible(firstHiddenUuid);
        }
    };

    handleMouseHover = (uuid, highlighted) => {
        if (this.props.onHighlightHistoryItem) {
            this.props.onHighlightHistoryItem(uuid, highlighted)
        }
    };

    render() {
        const {history, onToggleVisible, onReset, onDelete, onUndoReset, onUndoDelete, hasUndoReset, hasUndoDelete, rightAlignMenu, height} = this.props;
        const historyItems = [];
        historyItems.push(
            <li key="head">
                <div className="edit-history-undo">
                    {hasUndoDelete &&
                    <a
                        onClick={onUndoDelete} title="Undo delete" className="undoLink"
                        style={{float: 'right', fontSize: 9, marginTop: -3, padding: 0}}>{'undo'}
                    </a>
                    }
                </div>
            </li>
        );
        for (let i = history.length - 1; i >= 0; --i) {
            const historyItem = history[i];
            const {uuid, deleted, hidden, mode, brushSize, historyProps} = historyItem;
            if (deleted) {
                continue;
            }
            const eraseBoostLevel = maybe(historyProps ? historyProps.eraseBoostLevel : null, Defaults.ERASE_BOOST_LEVEL_MIN);
            const statusIcon = hidden ? <i className="fa fa-eye-slash"/> : <i className="fa fa-eye"/>;
            const title = hidden ? 'Show' : 'Hide';
            const descriptionStyle = hidden ? {textDecoration: 'line-through', opacity: 0.8} : {};
            const description = String(mode) + (mode === BRUSH ? ', ' + brushSize : '') + ', ' + eraseBoostLevel + '%';
            historyItems.push(
                <li key={2 * i} className="edit-history-item" onMouseEnter={() => this.handleMouseHover(uuid, true)}
                    onMouseLeave={() => this.handleMouseHover(uuid, false)}>
                    <Button bsStyle="link" bsSize="sm" className="delete-history-item" onClick={() => onDelete(uuid)} title="Delete">
                        <i className="fa fa-times"/>
                    </Button>
                    <Button bsStyle="link" bsSize="sm" className="hide-history-item" onClick={() => onToggleVisible(uuid)} title={title}>
                        {statusIcon}
                    </Button>
                    <div className="desc-history-item">
                        <a onClick={() => onToggleVisible(uuid)} title={title}><span style={descriptionStyle}>{description}</span></a>
                    </div>
                </li>);
        }
        const firstVisibleUuid = this.getFirstVisibleUuid();
        const firstHiddenUuid = this.getFirstHiddenUuid();
        const hasEdits = firstVisibleUuid || firstHiddenUuid;
        const tooltips = [];

        if (firstVisibleUuid) {
            tooltips.push(
                <ReactTooltip id="undoTooltip" key="undoTooltip" place="bottom" delayShow={400} className="light-tool-tip" type="light">
                    <div>{'Undo last edit'}</div>
                    <kbd>{isMac ? 'cmd z' : 'ctrl z'}</kbd>
                </ReactTooltip>
            );
        }
        if (firstHiddenUuid) {
            tooltips.push(
                <ReactTooltip id="redoTooltip" key="redoTooltip" place="bottom" delayShow={400} className="light-tool-tip" type="light">
                    <div>{'Redo last edit'}</div>
                    <kbd>{isMac ? 'cmd y' : 'ctrl y'}</kbd>
                </ReactTooltip>
            );
        }
        if (hasEdits) {
            tooltips.push(
                <ReactTooltip id="editsTooltip" key="editsTooltip" place="bottom" delayShow={400} className="light-tool-tip" type="light">
                    <div>{'Hide/show/delete edits'}</div>
                </ReactTooltip>
            );
            tooltips.push(
                <ReactTooltip id="resetTooltip" key="resetTooltip" place="bottom" delayShow={400} className="light-tool-tip" type="light">
                    <div>{'Reset all edits'}</div>
                </ReactTooltip>
            );
        }
        const dropUp = height < 750;

        return (
            <div className="edit-tools">
                <ButtonGroup className="edit-undo-redo">
                    <Button
                        onClick={this.handleUndo} disabled={!firstVisibleUuid} data-tip data-for="undoTooltip"
                        title={!firstVisibleUuid ? 'Undo' : ''}>
                        <i className="fa fa-undo"/>
                    </Button>
                    <Button
                        onClick={this.handleRedo} disabled={!firstHiddenUuid} data-tip data-for="redoTooltip"
                        title={!firstHiddenUuid ? 'Redo' : ''}>
                        <i className="fa fa-repeat"/>
                    </Button>
                </ButtonGroup>
                <div className="edit-history">
                    <Dropdown
                        id="editHistory" disabled={!hasEdits} data-tip data-for="editsTooltip"
                        title={!hasEdits ? 'Hide/show/delete edits' : ''} pullRight={rightAlignMenu} dropup={dropUp}>
                        <Dropdown.Toggle>{'Edits'}</Dropdown.Toggle>
                        <Dropdown.Menu style={{maxHeight: dropUp ? 500 : height / 2 - 300, overflowY: 'auto', overflowX: 'hidden'}} className="edit-items">
                            {historyItems}
                        </Dropdown.Menu>
                    </Dropdown>
                </div>
                <div className="edit-reset">
                    <Button
                        onClick={onReset} disabled={!hasEdits} data-tip data-for="resetTooltip"
                        title={!hasEdits ? 'Reset all edits' : ''}>
                        <i className="fa fa-step-backward"/>{' Reset'}
                    </Button>
                    <div className={classNames('edit-undo-reset', {invisible: !hasUndoReset})}>
                        <a onClick={onUndoReset} title="Undo Reset" className="undoLink">{'Undo'}</a>
                    </div>
                </div>
                {tooltips}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/EditHistory.js